/**
 * weeklyReportsService -- list + fetch single weekly report.
 *
 * Hits GET /weekly-reports backed by notches-weekly-reports-prod. Server
 * decides which audiences the caller can read based on entitlements.
 *
 * Audiences:
 *   - 'bb'      -- single global report per week (BB members)
 *   - 'notches' -- per-recipient report (Notches subscribers, scoped to caller)
 *
 * A user with both entitlements sees both audiences in tabs. The list call
 * returns separate arrays per audience; the fetch-one call requires the
 * audience to be specified.
 */
import axios from 'axios';

// Public read — prefer PUBLIC_API_URL so a local Stripe-test build still
// hits prod for the weekly-reports Lambda. See briefingsService for the
// same rationale.
const API_BASE_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export type ReportAudience = 'bb' | 'notches';

export interface WeeklyReportSummary {
  weekIso: string;          // 'YYYY-Www'
  weekLabel: string;        // 'Jun 08 - Jun 12, 2026'
  generatedAt: string;      // ISO-8601
}

export interface WeeklyReportListResponse {
  audiences: ReportAudience[];
  bb?: WeeklyReportSummary[];
  notches?: WeeklyReportSummary[];
}

export interface WeeklyReportFull {
  audience: ReportAudience;
  weekIso: string;
  weekLabel: string;
  generatedAt: string;
  htmlBody: string;
}

export const weeklyReportsService = {
  async list(token: string): Promise<WeeklyReportListResponse> {
    const resp = await axios.get<WeeklyReportListResponse>(
      `${API_BASE_URL}/weekly-reports`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return resp.data;
  },

  async get(token: string, audience: ReportAudience, weekIso: string): Promise<WeeklyReportFull> {
    const resp = await axios.get<WeeklyReportFull>(
      `${API_BASE_URL}/weekly-reports?audience=${audience}&week=${weekIso}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return resp.data;
  },
};
